/**
 * A tiny query cache (~100 lines) instead of @tanstack/react-query: the app needs keyed fetch dedupe, stale marking,
 * prefix invalidation driven by SSE, and optimistic setData — nothing else. Keys are "/"-separated strings
 * ("series", "series/<id>", "messages/<id>"); invalidating "series" also hits "series/<id>".
 */
export type Status = "idle" | "loading" | "success" | "error";
export type Snapshot<T> = { data: T | undefined; error: unknown; status: Status; fetching: boolean; updatedAt: number; stale: boolean };

type Entry = Snapshot<unknown> & { promise?: Promise<unknown>; fetcher?: () => Promise<unknown>; subscribers: number };

const EMPTY: Snapshot<never> = Object.freeze({ data: undefined, error: undefined, status: "idle", fetching: false, updatedAt: 0, stale: true });

export class QueryCache {
  private entries = new Map<string, Entry>();
  private listeners = new Map<string, Set<() => void>>();

  snapshot<T>(key: string): Snapshot<T> {
    return (this.entries.get(key) as Snapshot<T> | undefined) ?? (EMPTY as Snapshot<T>);
  }

  subscribe(key: string, fn: () => void): () => void {
    let set = this.listeners.get(key);
    if (!set) this.listeners.set(key, (set = new Set()));
    set.add(fn);
    return () => { set.delete(fn); };
  }

  /** Registers the fetcher a mounted component uses, so invalidation can refetch it. */
  attach(key: string, fetcher: () => Promise<unknown>): () => void {
    const e = this.ensure(key);
    e.fetcher = fetcher;
    e.subscribers++;
    return () => { const cur = this.entries.get(key); if (cur) cur.subscribers = Math.max(0, cur.subscribers - 1); };
  }

  fetch<T>(key: string, fetcher?: () => Promise<T>): Promise<T> {
    const e = this.ensure(key);
    if (fetcher) e.fetcher = fetcher;
    if (e.promise) return e.promise as Promise<T>;
    const run = e.fetcher;
    if (!run) return Promise.reject(new Error(`No fetcher for ${key}`));
    const promise = run().then(
      (data) => { this.write(key, { data, error: undefined, status: "success", fetching: false, stale: false, updatedAt: Date.now() }); return data as T; },
      (error: unknown) => { this.write(key, { error, status: this.snapshot(key).data === undefined ? "error" : "success", fetching: false }); throw error; },
    );
    this.write(key, { fetching: true, status: this.snapshot(key).data === undefined ? "loading" : this.snapshot(key).status });
    const cur = this.entries.get(key);
    if (cur) cur.promise = promise;
    promise.catch(() => undefined);
    return promise;
  }

  setData<T>(key: string, updater: T | ((prev: T | undefined) => T)): void {
    const prev = this.snapshot<T>(key).data;
    const data = typeof updater === "function" ? (updater as (p: T | undefined) => T)(prev) : updater;
    this.write(key, { data, status: "success", error: undefined, updatedAt: Date.now() });
  }

  /** Marks matching entries stale and refetches the ones on screen. `prefix` matches the key itself or "prefix/…". */
  invalidate(prefix: string | ((key: string) => boolean)): void {
    const match = typeof prefix === "function" ? prefix : (k: string) => k === prefix || k.startsWith(`${prefix}/`);
    for (const [key, e] of this.entries) {
      if (!match(key)) continue;
      this.write(key, { stale: true });
      if (e.subscribers > 0 && e.fetcher && !e.promise) void this.fetch(key).catch(() => undefined);
    }
  }

  invalidateAll(): void { this.invalidate(() => true); }

  clear(): void { this.entries.clear(); for (const set of this.listeners.values()) for (const fn of set) fn(); }

  private ensure(key: string): Entry {
    let e = this.entries.get(key);
    if (!e) { e = { ...EMPTY, subscribers: 0 }; this.entries.set(key, e); }
    return e;
  }

  private write(key: string, patch: Partial<Entry>): void {
    const prev = this.ensure(key);
    const next: Entry = { ...prev, ...patch };
    if (patch.fetching === false) next.promise = undefined;
    this.entries.set(key, next);
    for (const fn of this.listeners.get(key) ?? []) fn();
  }
}
